import { useContext, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputMask from "react-input-mask";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { ClientContext } from "../../state/ClientContext";
import { AuthContext } from "../../state/AuthContext";
import { toast } from "react-toastify";
import { apiRequest } from "../../api/baseApi";
import "../clientProfile/ClientDetailsForm.css";
import { US_STATES } from "../../util/defs";
import { getFirstName, getLastName } from "../../utils/fieldAccessors";

export default function CaregiverDetailsForm() {
  const { selectedCaregiver, refetchClients } = useContext(ClientContext);
  const [isEditable, setIsEditable] = useState(false);
  const { currentUser } = useContext(AuthContext);

  const responsibilityOptions = [
    { label: "Assisted Living Center", value: "Assisted Living Renter" },
    { label: "Behavioral Health", value: "Behavioral Health" },
    { label: "Psychiatric Professional", value: "Psychiatric Professional" },
    { label: "Group Home", value: "Group Home" },
    { label: "DO/MD Office", value: "DO/MD Office" },
    { label: "Aging Office", value: "Aging Office" },
    {
      label: "Certified Nursing Assistant",
      value: "Certified Nursing Assistant",
    },
    { label: "Licensed Practical Nurse", value: "Licensed Practical Nurse" },
    { label: "Registered Nurse", value: "Registered Nurse" },
    {
      label: "Physical or Occupational Therapist",
      value: "Physical or Occupational Therapist",
    },
    { label: "Friend or Family Member", value: "Friend or Family Member" },
    { label: "Speech Therapist", value: "Speech Therapist" },
    { label: "Support Coordinator", value: "Support Coordinator" },
    { label: "House Manager", value: "House Manager" },
    { label: "Other", value: "Other" },
  ];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: getFirstName(selectedCaregiver, ""),
      lastName: getLastName(selectedCaregiver, ""),
      title: selectedCaregiver?.title || "",
      companyName: selectedCaregiver?.company || "",
      responsibility: selectedCaregiver?.responsibility || "",
      email: selectedCaregiver?.email || "",
      phone: selectedCaregiver?.phone || "",
      city: selectedCaregiver?.city || "",
      state: selectedCaregiver?.state || "",
      zipCode: selectedCaregiver?.zipCode || "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Enter first name."),
      lastName: Yup.string().required("Enter last name."),
      title: Yup.string().required("Enter Title."),
      companyName: Yup.string().required("Enter Company Name."),
      responsibility: Yup.string().required("Enter Responsibility."),
      email: Yup.string().email("Enter valid email.").required("Enter email."),
      phone: Yup.string()
        .required("Enter phone number.")
        .matches(/^\+1\d{10}$/, "Enter valid U.S. phone number."),
      city: Yup.string().required("Enter city."),
      state: Yup.string().required("Select state."),
      zipCode: Yup.string()
        .required("Enter ZIP code.")
        .matches(/^\d{5}$/, "ZIP code must be 5 digits."),
    }),
    onSubmit: async (values) => {
      try {
        const response = await apiRequest(`/users/${selectedCaregiver.id}`, {
          version: "v2",
          method: "PUT",
          body: values,
        });
        if (response.ok) {
          toast.success("Update profile success");
          setIsEditable(false);
          await refetchClients();
        } else {
          console.error("Failed to update profile");
        }
      } catch (error) {
        console.error("Failed to update profile", error);
      }
    },
  });

  if (!selectedCaregiver) {
    return null;
  }

  const fields = [
    { label: "First Name", name: "firstName", type: "text" },
    { label: "Last Name", name: "lastName", type: "text" },
    { label: "Title", name: "title", type: "text" },
    { label: "Company Name", name: "companyName", type: "text" },
    { label: "Responsibility", name: "responsibility", type: "select" },
    { label: "Email", name: "email", type: "email", disabled: true },
    { label: "Phone", name: "phone", type: "text", mask: "+19999999999" },
    { label: "City", name: "city", type: "text" },
    { label: "State", name: "state", type: "select" },
    { label: "Zip Code", name: "zipCode", type: "text" },
  ];

  return (
    <div>
      <form className="client-form" onSubmit={formik.handleSubmit}>
        <div className="client-form-header">
          <h1>Caregiver details</h1>

          <div className="button-group">
            {currentUser?.isSuperAdmin || currentUser?.isAdmin ? (
              <button
                type="button"
                onClick={(ev) => {
                  ev.preventDefault();
                  if (isEditable) {
                    formik.resetForm();
                  }
                  setIsEditable(!isEditable);
                }}
              >
                {!isEditable ? <EditIcon /> : <CloseIcon />}
              </button>
            ) : null}
            {isEditable && (
              <button type="submit">
                <SaveIcon />
              </button>
            )}
          </div>
        </div>

        {fields.map((field) => (
          <div
            key={field.name}
            className={`client-form-row ${!isEditable ? "non-edit" : ""}`}
          >
            <label
              htmlFor={field.name}
              className={`client-form-label ${!isEditable ? "non-edit" : ""}`}
            >
              {field.label}:
            </label>

            <div
              className={`client-form-field ${!isEditable ? "non-edit" : ""}`}
            >
              {isEditable ? (
                field.mask ? (
                  <InputMask
                    mask={field.mask}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                  >
                    {(inputProps) => (
                      <input
                        {...inputProps}
                        type="text"
                        id={field.name}
                        name={field.name}
                        className="client-form-input"
                      />
                    )}
                  </InputMask>
                ) : field.type === "select" ? (
                  <select
                    id={field.name}
                    name={field.name}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                    className="client-form-select"
                    required
                  >
                    <option value="" disabled>
                      {field.name === "state"
                        ? "Select state"
                        : field.name === "responsibility"
                        ? "Select responsibility"
                        : "Select timezone"}
                    </option>

                    {field.name === "state" &&
                      US_STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}

                    {field.name === "responsibility" &&
                      responsibilityOptions.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    value={formik.values[field.name]}
                    onChange={formik.handleChange}
                    disabled={field.disabled}
                    className="client-form-input"
                  />
                )
              ) : field.type === "select" ? (
                <span className="client-form-readonly">
                  {field.name === "state"
                    ? formik.values.state
                    : responsibilityOptions.find(
                        ({ value }) => value === formik.values[field.name]
                      )?.label}
                </span>
              ) : (
                <span className="client-form-readonly">
                  {formik.values[field.name]}
                </span>
              )}

              {formik.touched[field.name] && formik.errors[field.name] && (
                <small className="client-form-error">
                  {formik.errors[field.name]}
                </small>
              )}
            </div>
          </div>
        ))}
      </form>
    </div>
  );
}
